// This screen shown inside the desktop app's screen (an iframe there): the
// app draws the rail, Personal and a workspace's sidebar (from this screen's
// data, the outside's contract over `frameShellHost`); this screen draws the
// panel beside it, or the whole of a page with a sidebar of its own (the
// settings), and hands pages of personal data to the app (what is sent to an
// agent is signed there, never here). The messages are ui-core's frame
// protocol.
//
// Promise:
// - It is that only when framed by the app (`location.ancestorOrigins`, the
//   app's own scheme); a message is read only from that window and origin.
// - Where it moves is told to the app (`location`), once drawn (`ready`); a
//   page of Me is handed over (`open-personal`) with the workspace around it
//   and that workspace's label; a lost session is told (`signed-out`).
// - The app's look, a page to go to, "the panel alone" and the window's
//   controls over this screen are followed.
// - Wheels this screen does not use and the mouse's back / forward buttons
//   go to the app (its window's back and forward).
// - A change to the person's workspaces is told to the app (its rail).
// - Once the app says who it is (`desktop`), this screen's notices are the
//   app's: this desktop is registered as their destination as before, and a
//   notice that would show is handed to the app (`notify`), which shows it as
//   its own notification unless its window shows that page. Nothing else of
//   the app's bridge is lent to this screen.
import { useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import {
  type DesktopNotificationStatus,
  type DesktopShell,
  defineScreenPart,
  type FrameTitleBar,
  type FrameToHost,
  isAppOrigin,
  parseHostToFrame,
  provideNoticeDesktop,
  readFromHost,
  type ShellHost,
  type TitleBarSource,
  themeSetting,
  useMeApp,
} from "@trameria/ui-core";
import { useEffect, useSyncExternalStore } from "react";
import { cloudsOwnMePath } from "./me-home/me-home";
import { mePlaceOf } from "./workspace/me-place";
import { workspacesKey } from "./workspace/queries";

/** The app's origin when this screen is framed by it, else null. */
export const desktopFrame: { origin: string } | null = (() => {
  try {
    if (window.top === window) return null;
    const origin = window.location.ancestorOrigins?.[0];
    return origin && isAppOrigin(origin) ? { origin } : null;
  } catch {
    return null;
  }
})();

export function postToHost(message: FrameToHost): void {
  if (!desktopFrame) return;
  window.parent.postMessage(message, desktopFrame.origin);
}

const SIGNED_OUT_TOLD = "trameria-frame:signed-out-at";
/** The span in which the app is told at most twice. */
export const SIGNED_OUT_SPAN_MS = 20_000;

/**
 * Whether to tell the app again that the session is gone: at most twice in
 * `SIGNED_OUT_SPAN_MS` across the page's loads (kept in the frame's session
 * storage). The second time is the app's sign that the session it made was
 * not taken (it then says so); an app that loads the page anew whatever
 * happened would otherwise go round.
 */
export function mayTellSignedOut(
  storage: Pick<Storage, "getItem" | "setItem">,
  now: number,
): boolean {
  const told = (storage.getItem(SIGNED_OUT_TOLD) ?? "")
    .split(",")
    .map(Number)
    .filter((at) => at > 0 && now - at < SIGNED_OUT_SPAN_MS);
  if (told.length >= 2) return false;
  storage.setItem(SIGNED_OUT_TOLD, [...told, now].join(","));
  return true;
}

/** Told once in this load of the page (many requests fail together). */
let toldHere = false;

/**
 * Tells the app this page's session is gone (it makes it again, then loads
 * this page anew). False when not told (`mayTellSignedOut`): the app could
 * not make it again.
 */
export function tellSignedOut(): boolean {
  if (!desktopFrame) return false;
  if (toldHere) return true;
  try {
    if (!mayTellSignedOut(sessionStorage, Date.now())) return false;
  } catch {
    // No storage: told once per load.
  }
  toldHere = true;
  postToHost({ type: "trameria-frame:signed-out" });
  return true;
}

/** How much to draw, as the app last asked (null: all of it). */
let layout: { kind: "panel" } | null = null;
const layoutListeners = new Set<() => void>();

/** MeExtensions.frameLayout for this screen in the app. */
export const frameLayout = {
  use: () =>
    useSyncExternalStore(
      (fn) => {
        layoutListeners.add(fn);
        return () => layoutListeners.delete(fn);
      },
      () => layout,
    ),
};

/**
 * Under a sideways scroller (a text field scrolling its text too) the wheel
 * is theirs, not a swipe: the same rule as the app's own screen.
 */
function overSideScroller(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  // Text fields scroll their text sideways; overflow does not show it.
  const field = target.closest("input, textarea");
  if (field && field.scrollWidth > field.clientWidth + 1) return true;
  const page = document.scrollingElement;
  for (let el: Element | null = target; el; el = el.parentElement) {
    if (el.scrollWidth <= el.clientWidth + 1) continue;
    const overflowX = getComputedStyle(el).overflowX;
    if (overflowX === "auto" || overflowX === "scroll") return true;
    if (el === page && overflowX !== "hidden" && overflowX !== "clip")
      return true;
  }
  return false;
}

/**
 * The outside's contract over the app's frame (Me's `shellHost`): the menu,
 * with the workspace's sidebar as data, goes to the app, which draws the
 * sidebar; what is pressed there comes back. Not embedded: this screen keeps
 * its own headers and draws the panel alone (`frameLayout`).
 */
export const frameShellHost: ShellHost | null = desktopFrame
  ? {
      embedded: false,
      post: (message) =>
        postToHost({
          type: "trameria-frame:shell",
          message: message as unknown as Record<string, unknown>,
        }),
      subscribe(listener) {
        const host = desktopFrame?.origin;
        const onMessage = (event: MessageEvent) => {
          if (event.source !== window.parent || event.origin !== host) return;
          const message = parseHostToFrame(event.data);
          if (message?.type !== "trameria-host:shell") return;
          const read = readFromHost(message.message);
          if (read) listener(read);
        };
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
      },
    }
  : null;

/** The window's controls over this screen, as the app last said (null: none). */
let titleBarArea: FrameTitleBar | null = null;
const titleBarListeners = new Set<() => void>();

/** MeExtensions.titleBar for this screen in the app: the controls the app's window has over it. */
export const frameTitleBar: TitleBarSource = {
  get: () => titleBarArea,
  subscribe: (fn) => {
    titleBarListeners.add(fn);
    return () => titleBarListeners.delete(fn);
  },
};

/** The app as this screen's notices see it (made once it says who it is). */
let handed: {
  shell: DesktopShell;
  setStatus(s: DesktopNotificationStatus): void;
} | null = null;

/** Notices go to the app: what `showOsNotice` and the settings use here. */
function handNoticesToApp(
  installationId: string,
  status: DesktopNotificationStatus,
) {
  if (handed?.shell.installationId === installationId) {
    handed.setStatus(status);
    return;
  }
  let now = status;
  const listeners = new Set<(s: DesktopNotificationStatus) => void>();
  const shell: DesktopShell = {
    installationId,
    notify: ({ title, body, url, tag, actions }) =>
      postToHost({
        type: "trameria-frame:notify",
        title,
        body,
        url,
        tag,
        ...(actions ? { actions } : {}),
      }),
    closeNotifications: (tag) =>
      postToHost({ type: "trameria-frame:notices-clear", tag }),
    notificationStatus: () => now,
    onNotificationStatusChange(handler) {
      listeners.add(handler);
      return () => void listeners.delete(handler);
    },
    openNotificationSettings: () =>
      postToHost({ type: "trameria-frame:notification-settings" }),
  };
  handed = {
    shell,
    setStatus(next) {
      if (next === now) return;
      now = next;
      for (const fn of listeners) fn(next);
    },
  };
  provideNoticeDesktop(shell);
}

/** Follows the app's messages and tells it where this screen is (a part's Background). */
function DesktopFrameFollower(): null {
  const router = useRouter();
  const app = useMeApp();
  const qc = useQueryClient();
  // The person's workspaces as this screen last read them (made, joined,
  // left, renamed here or elsewhere, by a mutation or the realtime feed):
  // the app's rail reads its list again when they change.
  // frontend-development: telling the app the workspaces changed, a side effect / none: the rail is the app's
  useEffect(() => {
    if (!desktopFrame) return;
    let seen: string | null = null;
    return qc.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "success") return;
      const key = event.query.queryKey;
      if (key.length !== workspacesKey.length) return;
      if (!workspacesKey.every((part, i) => key[i] === part)) return;
      const now = JSON.stringify(event.query.state.data ?? null);
      const before = seen;
      seen = now;
      if (before !== null && before !== now)
        postToHost({ type: "trameria-frame:workspaces" });
    });
  }, [qc]);
  // frontend-development: telling the app where the page is and hearing it, side effects / none: the app has no other way to know
  useEffect(() => {
    if (!desktopFrame) return;
    const host = desktopFrame.origin;
    const onMessage = (event: MessageEvent) => {
      if (event.source !== window.parent || event.origin !== host) return;
      const message = parseHostToFrame(event.data);
      if (!message) return;
      if (message.type === "trameria-host:preload") {
        // A link of the app's under the pointer: this page's data ahead.
        const to = new URL(message.href, window.location.origin).pathname;
        void router.preloadRoute({ to }).catch(() => {});
      } else if (message.type === "trameria-host:navigate") {
        const now = `${router.state.location.pathname}${router.state.location.searchStr}`;
        if (now !== message.href) void router.navigate({ href: message.href });
      } else if (message.type === "trameria-host:theme") {
        themeSetting.write(message.theme);
      } else if (message.type === "trameria-host:desktop") {
        handNoticesToApp(message.installationId, message.notificationStatus);
      } else if (message.type === "trameria-host:title-bar") {
        titleBarArea = message.area;
        for (const fn of titleBarListeners) fn();
      } else if (message.type === "trameria-host:layout") {
        layout = message.layout.kind === "full" ? null : message.layout;
        for (const fn of layoutListeners) fn();
      }
    };
    window.addEventListener("message", onMessage);
    // Back and forward are the app's window's: the swipe it reads from wheel
    // events and the mouse's buttons, which land here over this screen.
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.defaultPrevented || overSideScroller(e.target)) return;
      postToHost({
        type: "trameria-frame:wheel",
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaMode: e.deltaMode,
      });
    };
    const onMouseUp = (e: MouseEvent) => {
      if (e.button !== 3 && e.button !== 4) return;
      e.preventDefault();
      postToHost({
        type: "trameria-frame:history-button",
        direction: e.button === 3 ? "back" : "forward",
      });
    };
    const onMouseDown = (e: MouseEvent) => {
      if (e.button === 3 || e.button === 4) e.preventDefault();
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousedown", onMouseDown);
    const tell = () => {
      const { pathname, searchStr, hash } = router.state.location;
      const href = `${pathname}${searchStr}`;
      // The router's history is in memory, so the frame's own address stays
      // where it was first loaded. A reload (a newer screen, a retry) would
      // land there instead of on this page: keep the address in step. A
      // replace adds no step to the window's history.
      const at = `${href}${hash ? `#${hash}` : ""}`;
      const { pathname: p, search: s, hash: h } = window.location;
      if (`${p}${s}${h}` !== at) window.history.replaceState(null, "", at);
      postToHost({ type: "trameria-frame:location", href });
      // A page of Me (not the settings, not the cloud's own direct
      // messages) is the app's to show, with the workspace around it.
      if (
        /^\/me(?:[/?#]|$)/.test(pathname) &&
        !pathname.startsWith("/me/settings") &&
        !cloudsOwnMePath(pathname)
      ) {
        const workspace = mePlaceOf(pathname);
        postToHost({
          type: "trameria-frame:open-personal",
          href,
          workspace,
          workspaceId: workspace ? app.workspaceFilter.get() : null,
        });
      }
    };
    // After the page's layout effects (a workspace sets its id then).
    const off = router.subscribe("onResolved", () =>
      requestAnimationFrame(tell),
    );
    tell();
    postToHost({ type: "trameria-frame:ready" });
    return () => {
      window.removeEventListener("message", onMessage);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousedown", onMouseDown);
      off();
    };
  }, [router, app]);
  return null;
}

/**
 * In place of a page of Me inside the app: the app shows it (the follower
 * handed it over); nothing is drawn here.
 */
export function HandToApp() {
  return <div className="h-full bg-surface" />;
}

/** What this screen does inside the app (only there). */
export const desktopFramePart = defineScreenPart({
  id: "desktop-frame",
  Background: DesktopFrameFollower,
});
